/**
 * HomepageSubSection — Phase 18.15.7
 *
 * One-stop admin form for everything the public HomePage.jsx renders
 * that we made editable in the backoffice:
 *   - Hero (eyebrow / title / subtitle / 3 CTAs / trust bullets)
 *   - Service cards (full CRUD with sort + activate/deactivate)
 *   - Section headers (services, network, routes header, trusted brands header)
 *   - CTA band
 *
 * Backend endpoints:
 *   GET  /api/admin/homepage-settings
 *   PUT  /api/admin/homepage-settings/{section}
 *   GET  /api/admin/homepage-service-cards
 *   POST /api/admin/homepage-service-cards
 *   PUT  /api/admin/homepage-service-cards/{id}
 *   POST /api/admin/homepage-service-cards/reorder
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { toast } from "sonner";
import {
  Save, Plus, ChevronUp, ChevronDown, Loader2, Trash2, Edit3,
  Sparkles, LayoutGrid, Network, Megaphone, Award, Eye, X,
} from "lucide-react";

const ICON_OPTIONS = [
  "ship", "plane", "package", "truck", "car", "map", "briefcase", "user",
  "check", "clock", "shield", "banknote", "support", "info",
];

const SECTION_DEFS = [
  { key: "hero",                  label: "Hero",                    icon: Sparkles },
  { key: "services_header",       label: "Services kop",            icon: LayoutGrid },
  { key: "network",               label: "Network",                 icon: Network },
  { key: "routes_header",         label: "Routes kop",              icon: Network },
  { key: "trusted_brands_header", label: "Trusted Brands kop",      icon: Award },
  { key: "cta_band",              label: "Onderste CTA-band",       icon: Megaphone },
];

export default function HomepageSubSection() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [sections, setSections] = useState(null);
  const [activeSection, setActiveSection] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);

  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [editCard, setEditCard] = useState(null); // {id?, form}
  const [savingCard, setSavingCard] = useState(false);

  // ---- Load settings + service cards ----
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        adminApi.get("/admin/homepage-settings"),
        adminApi.get("/admin/homepage-service-cards"),
      ]);
      setSections(s.data?.sections || {});
      setCards(Array.isArray(c.data?.cards) ? c.data.cards : []);
    } catch (e) {
      toast.error(formatApiError(e, "Kon homepage-instellingen niet laden"));
    } finally {
      setLoading(false);
      setCardsLoading(false);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- Section editing ----
  const updateSectionField = (sectionKey, field, value) => {
    setSections((s) => ({
      ...s,
      [sectionKey]: { ...(s?.[sectionKey] || {}), [field]: value },
    }));
  };

  const updateListItem = (sectionKey, listKey, index, field, value) => {
    setSections((s) => {
      const list = Array.isArray(s?.[sectionKey]?.[listKey]) ? [...s[sectionKey][listKey]] : [];
      list[index] = { ...(list[index] || {}), [field]: value };
      return { ...s, [sectionKey]: { ...s[sectionKey], [listKey]: list } };
    });
  };
  const addListItem = (sectionKey, listKey, template) => {
    setSections((s) => {
      const list = Array.isArray(s?.[sectionKey]?.[listKey]) ? [...s[sectionKey][listKey]] : [];
      list.push({ ...template });
      return { ...s, [sectionKey]: { ...s[sectionKey], [listKey]: list } };
    });
  };
  const removeListItem = (sectionKey, listKey, index) => {
    setSections((s) => {
      const list = Array.isArray(s?.[sectionKey]?.[listKey]) ? [...s[sectionKey][listKey]] : [];
      list.splice(index, 1);
      return { ...s, [sectionKey]: { ...s[sectionKey], [listKey]: list } };
    });
  };

  const saveSection = async (sectionKey) => {
    if (!canEdit) return;
    setSavingSection(sectionKey);
    try {
      const r = await adminApi.put(`/admin/homepage-settings/${sectionKey}`, {
        payload: sections[sectionKey] || {},
      });
      setSections(r.data?.sections || sections);
      toast.success(`${labelOf(sectionKey)} opgeslagen`);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingSection(null);
    }
  };

  // ---- Service cards CRUD ----
  const openNewCard = () => setEditCard({
    id: null,
    title: "", description: "", badge: "", icon: "package",
    link_url: "", sort_order: (cards.length + 1) * 10, is_active: true,
  });
  const openEditCard = (card) => setEditCard({ ...card });

  const saveCard = async () => {
    if (!editCard) return;
    if (!editCard.title || !editCard.title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    setSavingCard(true);
    try {
      const body = {
        title: editCard.title.trim(),
        description: editCard.description || "",
        badge: editCard.badge || "",
        icon: editCard.icon || "package",
        link_url: editCard.link_url || "",
        sort_order: Number(editCard.sort_order) || 100,
        is_active: !!editCard.is_active,
      };
      if (editCard.id) {
        const r = await adminApi.put(`/admin/homepage-service-cards/${editCard.id}`, body);
        setCards((cs) => cs.map((c) => (c.id === editCard.id ? r.data.card : c)));
        toast.success("Service card opgeslagen");
      } else {
        const r = await adminApi.post("/admin/homepage-service-cards", body);
        setCards((cs) => [...cs, r.data.card].sort((a, b) => a.sort_order - b.sort_order));
        toast.success("Service card aangemaakt");
      }
      setEditCard(null);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingCard(false);
    }
  };

  const toggleCardActive = async (card) => {
    try {
      const r = await adminApi.put(`/admin/homepage-service-cards/${card.id}`, {
        is_active: !card.is_active,
      });
      setCards((cs) => cs.map((c) => (c.id === card.id ? r.data.card : c)));
      toast.success(card.is_active ? "Card gedeactiveerd" : "Card geactiveerd");
    } catch (e) {
      toast.error(formatApiError(e, "Wijzigen mislukt"));
    }
  };

  const moveCard = async (card, direction) => {
    const idx = cards.findIndex((c) => c.id === card.id);
    const target = idx + direction;
    if (target < 0 || target >= cards.length) return;
    const reordered = [...cards];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    // Re-assign 10-step sort orders
    const payload = reordered.map((c, i) => ({ id: c.id, sort_order: (i + 1) * 10 }));
    setCards(reordered.map((c, i) => ({ ...c, sort_order: (i + 1) * 10 })));
    try {
      await adminApi.post("/admin/homepage-service-cards/reorder", { cards: payload });
    } catch (e) {
      toast.error(formatApiError(e, "Sortering mislukt"));
      loadAll(); // reload truth on failure
    }
  };

  if (loading || !sections) {
    return <div className="py-10 text-center text-gray-400 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Homepage-instellingen laden...</div>;
  }

  const active = sections[activeSection] || {};

  return (
    <div data-testid="homepage-sub-section">
      {!canEdit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      {/* Tab nav */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-3">
        {SECTION_DEFS.map((s) => {
          const Icon = s.icon;
          const isActive = activeSection === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setActiveSection(s.key)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-[#082c50] text-white"
                  : "bg-slate-100 text-[#082c50] hover:bg-slate-200"
              }`}
              data-testid={`homepage-tab-${s.key}`}
            >
              <Icon className="w-4 h-4" />
              {s.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setActiveSection("__service_cards__")}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
            activeSection === "__service_cards__"
              ? "bg-[#082c50] text-white"
              : "bg-slate-100 text-[#082c50] hover:bg-slate-200"
          }`}
          data-testid="homepage-tab-service-cards"
        >
          <LayoutGrid className="w-4 h-4" />
          Service cards
        </button>
      </div>

      {/* Section editor */}
      {activeSection !== "__service_cards__" && (
        <SectionEditor
          sectionKey={activeSection}
          values={active}
          onChange={(field, value) => updateSectionField(activeSection, field, value)}
          onListChange={(listKey, idx, field, value) => updateListItem(activeSection, listKey, idx, field, value)}
          onListAdd={(listKey, tpl) => addListItem(activeSection, listKey, tpl)}
          onListRemove={(listKey, idx) => removeListItem(activeSection, listKey, idx)}
          canEdit={canEdit}
          saving={savingSection === activeSection}
          onSave={() => saveSection(activeSection)}
        />
      )}

      {/* Service cards manager */}
      {activeSection === "__service_cards__" && (
        <div className="space-y-4" data-testid="homepage-service-cards-manager">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-chivo font-bold text-lg text-[#082c50]">Service cards</h3>
              <p className="text-xs text-gray-600 mt-1">Beheer de 8-tile bento grid onder de hero. Sortering via pijltjes; uitschakelen via toggle.</p>
            </div>
            {canEdit && (
              <Button onClick={openNewCard} className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white" data-testid="homepage-card-add">
                <Plus className="w-4 h-4 mr-1" /> Nieuwe card
              </Button>
            )}
          </div>
          {cardsLoading ? (
            <div className="py-8 text-center text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cards laden...</div>
          ) : (
            <div className="space-y-2">
              {cards.map((card, i) => (
                <div
                  key={card.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${card.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-70"}`}
                  data-testid={`homepage-card-row-${card.id}`}
                >
                  <div className="flex flex-col gap-1">
                    <button type="button" disabled={!canEdit || i === 0} onClick={() => moveCard(card, -1)} className="p-1 text-slate-500 hover:text-[#082c50] disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button type="button" disabled={!canEdit || i === cards.length - 1} onClick={() => moveCard(card, 1)} className="p-1 text-slate-500 hover:text-[#082c50] disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#082c50] via-[#1a5193] to-[#194d8d] text-white flex items-center justify-center text-[10px] font-bold uppercase">
                    {card.icon || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-chivo font-bold text-sm text-[#082c50] truncate">{card.title}</div>
                    <div className="text-xs text-gray-600 truncate">{card.description}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{card.link_url} · {card.badge || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={card.is_active}
                      disabled={!canEdit}
                      onCheckedChange={() => toggleCardActive(card)}
                      data-testid={`homepage-card-toggle-${card.id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canEdit}
                      onClick={() => openEditCard(card)}
                      className="rounded-xl"
                      data-testid={`homepage-card-edit-${card.id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {cards.length === 0 && (
                <div className="py-10 text-center text-gray-500 border border-dashed border-slate-300 rounded-xl">
                  Nog geen cards. Klik <strong>Nieuwe card</strong> om te beginnen.
                </div>
              )}
            </div>
          )}

          {/* Card edit modal-ish inline panel */}
          {editCard && (
            <div className="mt-4 p-5 rounded-2xl border border-[#082c50]/30 bg-blue-50/50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-chivo font-bold text-[#082c50]">
                  {editCard.id ? "Card bewerken" : "Nieuwe card"}
                </h4>
                <button type="button" onClick={() => setEditCard(null)} className="p-1 text-slate-500 hover:text-[#082c50]"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Titel</Label>
                  <Input value={editCard.title || ""} onChange={(e) => setEditCard({ ...editCard, title: e.target.value })} data-testid="homepage-card-title-input" />
                </div>
                <div className="col-span-2">
                  <Label>Beschrijving</Label>
                  <Textarea value={editCard.description || ""} onChange={(e) => setEditCard({ ...editCard, description: e.target.value })} rows={2} data-testid="homepage-card-desc-input" />
                </div>
                <div>
                  <Label>Badge / korte label</Label>
                  <Input value={editCard.badge || ""} onChange={(e) => setEditCard({ ...editCard, badge: e.target.value })} placeholder="Indicatieve planning" />
                </div>
                <div>
                  <Label>Icon</Label>
                  <select
                    value={editCard.icon || "package"}
                    onChange={(e) => setEditCard({ ...editCard, icon: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm"
                    data-testid="homepage-card-icon-select"
                  >
                    {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Link URL</Label>
                  <Input value={editCard.link_url || ""} onChange={(e) => setEditCard({ ...editCard, link_url: e.target.value })} placeholder="/sea-freight" />
                </div>
                <div>
                  <Label>Sortering</Label>
                  <Input type="number" value={editCard.sort_order || 100} onChange={(e) => setEditCard({ ...editCard, sort_order: e.target.value })} />
                </div>
                <div className="flex items-end gap-2">
                  <Switch checked={!!editCard.is_active} onCheckedChange={(v) => setEditCard({ ...editCard, is_active: v })} data-testid="homepage-card-active-switch" />
                  <span className="text-sm text-[#082c50]">Actief op homepage</span>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setEditCard(null)} className="rounded-xl">Annuleren</Button>
                <Button
                  onClick={saveCard}
                  disabled={!canEdit || savingCard}
                  className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
                  data-testid="homepage-card-save"
                >
                  {savingCard ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</> : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function labelOf(key) {
  return (SECTION_DEFS.find((s) => s.key === key) || {}).label || key;
}

// ============================================================
// SectionEditor — renders the right form for the chosen section
// ============================================================

// IMPORTANT: this wrapper sits OUTSIDE the SectionEditor body so React
// keeps a stable component identity across keystrokes. Defining it
// inline (e.g. `const Wrapper = ({ children }) => ...`) inside the
// parent component creates a brand-new component on every render →
// React unmounts the form → input focus is lost after every letter.
function SectionWrapper({ sectionKey, canEdit, saving, onSave, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid={`homepage-editor-${sectionKey}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-chivo font-bold text-lg text-[#082c50]">{labelOf(sectionKey)}</h3>
        <Button onClick={onSave} disabled={!canEdit || saving} className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white" data-testid={`homepage-save-${sectionKey}`}>
          {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</> : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
        </Button>
      </div>
      {children}
    </div>
  );
}

function SectionEditor({ sectionKey, values, onChange, onListChange, onListAdd, onListRemove, canEdit, saving, onSave }) {
  const wrap = (children) => (
    <SectionWrapper sectionKey={sectionKey} canEdit={canEdit} saving={saving} onSave={onSave}>
      {children}
    </SectionWrapper>
  );

  if (sectionKey === "hero") {
    return wrap(
      <>
        <TextRow label="Eyebrow (badge tekst)" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} testid="hero-eyebrow" />
        <TextRow label="H1 - hoofdtekst (begin)" v={values.title_lead} onChange={(v) => onChange("title_lead", v)} testid="hero-title-lead" />
        <TextRow label="H1 - highlight (gradient gedeelte)" v={values.title_highlight} onChange={(v) => onChange("title_highlight", v)} testid="hero-title-highlight" />
        <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} testid="hero-subtitle" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextRow label="Primaire CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} />
          <TextRow label="Primaire CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} />
          <TextRow label="Secundaire CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} />
          <TextRow label="Secundaire CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} />
          <TextRow label="Tertiaire CTA label" v={values.cta_tertiary_label} onChange={(v) => onChange("cta_tertiary_label", v)} />
          <TextRow label="Tertiaire CTA URL" v={values.cta_tertiary_url} onChange={(v) => onChange("cta_tertiary_url", v)} />
        </div>
        <ListEditor
          label="Trust bullets (onder hero)"
          items={values.trust_bullets || []}
          fields={[
            { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
            { key: "label", label: "Label", type: "text" },
          ]}
          onChange={(idx, field, v) => onListChange("trust_bullets", idx, field, v)}
          onAdd={() => onListAdd("trust_bullets", { icon: "check", label: "" })}
          onRemove={(idx) => onListRemove("trust_bullets", idx)}
          canEdit={canEdit}
          max={6}
        />
      </>
    );
  }

  if (sectionKey === "services_header") {
    return wrap(
      <>
        <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} />
        <TextRow label="Sectietitel (H2)" v={values.title} onChange={(v) => onChange("title", v)} />
        <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextRow label="Right-side link label" v={values.link_label} onChange={(v) => onChange("link_label", v)} />
          <TextRow label="Right-side link URL" v={values.link_url} onChange={(v) => onChange("link_url", v)} />
        </div>
      </>
    );
  }

  if (sectionKey === "network") {
    return wrap(
      <>
        <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} />
        <TextRow label="Sectietitel (H2)" v={values.title} onChange={(v) => onChange("title", v)} />
        <AreaRow label="Subtitle (paragraaf links)" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} />
        <ListEditor
          label="KPI tegels (max 3 ideaal)"
          items={values.kpis || []}
          fields={[
            { key: "value", label: "Waarde (bv. 15+)", type: "text" },
            { key: "label", label: "Label", type: "text" },
          ]}
          onChange={(idx, field, v) => onListChange("kpis", idx, field, v)}
          onAdd={() => onListAdd("kpis", { value: "", label: "" })}
          onRemove={(idx) => onListRemove("kpis", idx)}
          canEdit={canEdit}
          max={6}
        />
      </>
    );
  }

  if (sectionKey === "routes_header") {
    return wrap(
      <>
        <TextRow label="Eyebrow (boven routes tabel)" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} />
        <TextRow label="Tip tekst (rechtsboven)" v={values.tip_text} onChange={(v) => onChange("tip_text", v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextRow label="CTA label" v={values.cta_label} onChange={(v) => onChange("cta_label", v)} />
          <TextRow label="CTA URL" v={values.cta_url} onChange={(v) => onChange("cta_url", v)} />
        </div>
      </>
    );
  }

  if (sectionKey === "trusted_brands_header") {
    return wrap(
      <>
        <TextRow label="Eyebrow (kleine kop)" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} />
        <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} />
      </>
    );
  }

  if (sectionKey === "cta_band") {
    return wrap(
      <>
        <TextRow label="H3 titel" v={values.title} onChange={(v) => onChange("title", v)} />
        <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextRow label="Primaire CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} />
          <TextRow label="Primaire CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} />
          <TextRow label="Secundaire CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} />
          <TextRow label="Secundaire CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} />
        </div>
        <ListEditor
          label="Trust badges (3 tegels)"
          items={values.badges || []}
          fields={[
            { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
            { key: "title", label: "Titel", type: "text" },
            { key: "subtitle", label: "Subtitle", type: "text" },
          ]}
          onChange={(idx, field, v) => onListChange("badges", idx, field, v)}
          onAdd={() => onListAdd("badges", { icon: "check", title: "", subtitle: "" })}
          onRemove={(idx) => onListRemove("badges", idx)}
          canEdit={canEdit}
          max={6}
        />
      </>
    );
  }

  return wrap(<div className="text-sm text-gray-500">Selecteer een sectie.</div>);
}

function TextRow({ label, v, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid ? `homepage-${testid}-input` : undefined} />
    </div>
  );
}
function AreaRow({ label, v, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea rows={3} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid ? `homepage-${testid}-input` : undefined} />
    </div>
  );
}

function ListEditor({ label, items, fields, onChange, onAdd, onRemove, canEdit, max }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#082c50]">{label}</span>
        {canEdit && items.length < (max || 12) && (
          <Button size="sm" variant="outline" onClick={onAdd} className="rounded-lg text-xs">
            <Plus className="w-3 h-3 mr-1" /> Toevoegen
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-slate-200">
            {fields.map((f) => (
              <div key={f.key} className="flex-1 min-w-[140px]">
                <Label className="text-[10px] uppercase tracking-wide">{f.label}</Label>
                {f.type === "select" ? (
                  <select
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
                    disabled={!canEdit}
                  >
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    disabled={!canEdit}
                  />
                )}
              </div>
            ))}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onRemove(idx)} className="rounded-lg text-red-600">
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-gray-500 italic py-2">Nog geen items.</div>
        )}
      </div>
    </div>
  );
}
